import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Batch, BatchSummaryExpand } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";

import { useDefaultCurrency } from "../../../../shared/hooks/useDefaultCurrency";
import { selectorPayableImportBatchName } from "../../selectors/selectorPayableImportBatchName";
import { selectorPayableImportBatchStatusPill } from "../../selectors/selectorPayableImportBatchStatusPill";

/**
 * The summary is a per-batch call rather than something the list response
 * carries, so both cells that need it fetch it at the point of use — React
 * Query serves the second one from the same cache entry.
 */
const useQueryBatchSummary = (batchId?: string) =>
  queriesBatches.getBatchSummary.useQuery(
    { batchId: batchId as string, expand: [BatchSummaryExpand.BREAKDOWN] },
    { enabled: !!batchId }
  );

const CellImportTotal: React.FC<{ batchId?: string }> = props => {
  const querySummary = useQueryBatchSummary(props.batchId);
  const currency = useDefaultCurrency();
  const breakdown = querySummary.data?.breakdown;

  if (!breakdown || !("netAmount" in breakdown)) {
    return <WSTableCell text={querySummary.isLoading ? "" : "--"} />;
  }

  return (
    <WSTableCell
      text={formatMoney(breakdown.netAmount, currency)}
      secondaryText={`${breakdown.payablesCount} payables · ${breakdown.deductionsCount} deductions`}
    />
  );
};

const CellImportStatus: React.FC<{ batch: Batch }> = props => {
  const querySummary = useQueryBatchSummary(props.batch.id);

  return (
    <WSTableCell
      pill={selectorPayableImportBatchStatusPill({
        batch: props.batch,
        itemCount: querySummary.data?.progress?.total,
        failedCount: querySummary.data?.progress?.failed,
        showIcon: true
      })}
    />
  );
};

export const getColumns = (): WSTableColumn<Batch>[] => [
  {
    config: {
      gridTemplateSizeMax: "0.5fr",
      justify: "start",
      header: { text: "Created" }
    },
    renderFunction: rowItem => (
      <WSTableCell
        text={
          rowItem.data.events?.createdAt
            ? toWSDateString(rowItem.data.events.createdAt, "monthDayYear")
            : "--"
        }
      />
    )
  },
  {
    config: {
      gridTemplateSizeMax: "1fr",
      justify: "start",
      header: { text: "Name" }
    },
    renderFunction: rowItem => (
      <WSTableCell text={selectorPayableImportBatchName(rowItem.data)} />
    )
  },
  {
    config: {
      gridTemplateSizeMax: "0.6fr",
      justify: "start",
      header: { text: "Import total" },
      hideOnScreens: ["XS"]
    },
    renderFunction: rowItem => <CellImportTotal batchId={rowItem.data.id} />
  },
  {
    config: {
      gridTemplateSizeMax: "0.5fr",
      justify: "start",
      header: { text: "Import status" }
    },
    renderFunction: rowItem => <CellImportStatus batch={rowItem.data} />
  }
];
